import { useState, useEffect, useContext } from 'react';
import { ShoppingCart, User, Menu, X, LayoutDashboard } from 'lucide-react';
import { Link, NavLink } from 'react-router-dom';
import { AuthContext } from '../authProvider/AuthProvider';
import logo from '../assets/Size wise logo.png'

const MenuBar = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const { logOutUser, isAuthenticated} = useContext(AuthContext);

    const handleLogOut = () => {
        logOutUser();
        setIsMobileMenuOpen(false); // Close mobile menu if open
    };

    // Handle scroll effect
    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 20) {
                setIsScrolled(true);
            } else {
                setIsScrolled(false);
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const navLinks = [
        { name: 'Flavors', href: '/flavors' },
        { name: 'Toppings', href: '/toppings' },
        { name: 'Our Story', href: '/story' },
    ];

    return (
        <>
            <nav className={`fixed left-0 right-0 z-50 transition-all duration-300 ease-in-out ${isScrolled ? 'top-0' : 'top-4'}`}>
                <div className={`mx-auto max-w-7xl transition-all duration-300 ${isScrolled ? 'px-4' : 'px-4 md:px-8'}`}>

                    {/* Main Container */}
                    <div className={`relative flex items-center justify-between px-6 py-3 transition-all duration-300 border shadow-xl backdrop-blur-md ${isScrolled
                        ? 'bg-white/90 border-b-pink-200 rounded-b-2xl rounded-t-none'
                        : 'bg-white/80 border-white/40 rounded-full'
                        }`}>

                        {/* --- LOGO SECTION --- */}
                        <div className="flex items-center gap-2 group cursor-pointer">
                            <div className="p-2 group-hover:rotate-12 transition-transform duration-300">
                                <img src={logo} alt="Jilapi Logo" className="w-12 h-12" />
                            </div>
                            <Link to="/">
                                <span className="text-2xl font-bold bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent tracking-tight">
                                    jilapi
                                </span>
                            </Link>
                        </div>

                        {/* --- DESKTOP LINKS --- */}
                        <div className="hidden md:flex items-center space-x-8">
                            {navLinks.map((link) => (
                                <NavLink
                                    key={link.name}
                                    to={link.href}
                                    className={({ isActive }) => `font-medium hover:text-pink-500 transition-colors relative group ${isActive ? "text-pink-500" : "text-gray-600"}`}
                                >
                                    {link.name}
                                    <span className="absolute -bottom-1 left-0 w-0 h-1 bg-pink-400 rounded-full transition-all group-hover:w-full"></span>
                                </NavLink>
                            ))}
                            
                            {/* ✅ DASHBOARD LINK (Desktop) - Only shows if logged in */}
                            {isAuthenticated && (
                                <NavLink 
                                    to="/admin" 
                                    className={({ isActive }) => `flex items-center gap-1 font-bold transition-colors ${isActive ? "text-purple-600" : "text-gray-600 hover:text-purple-600"}`}
                                >
                                    <LayoutDashboard size={16} /> Dashboard
                                </NavLink>
                            )}
                        </div>

                        {/* --- ACTIONS (Cart & Login) --- */}
                        <div className="hidden md:flex items-center gap-4">

                            {/* Cart Icon */}
                            <button className="relative p-2 hover:bg-pink-50 rounded-full transition-colors group">
                                <ShoppingCart className="w-6 h-6 text-gray-700 group-hover:text-pink-600" />
                                <span className="absolute top-0 right-0 w-4 h-4 bg-gradient-to-r from-pink-500 to-purple-500 text-white text-[10px] font-bold flex items-center justify-center rounded-full border-2 border-white animate-bounce">
                                    3
                                </span>
                            </button>

                            {/* Login / Logout Button */}
                            {isAuthenticated ? (
                                <button onClick={handleLogOut} className="flex items-center gap-2 px-5 py-2.5 font-bold text-white transition-all shadow-lg rounded-full bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 hover:shadow-pink-500/30 hover:scale-105 active:scale-95">
                                    <User className="w-4 h-4" />
                                    <span>Logout</span>
                                </button>
                            ) : (
                                <Link to={'/login'}>
                                    <button className="flex items-center gap-2 px-5 py-2.5 font-bold text-white transition-all shadow-lg rounded-full bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 hover:shadow-pink-500/30 hover:scale-105 active:scale-95">
                                        <User className="w-4 h-4" />
                                        <span>Login</span>
                                    </button>
                                </Link>
                            )}
                        </div>

                        {/* --- MOBILE HAMBURGER --- */}
                        <div className="md:hidden">
                            <button
                                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                                className="p-2 text-gray-600 hover:bg-pink-50 rounded-full"
                            >
                                {isMobileMenuOpen ? <X /> : <Menu />}
                            </button>
                        </div>
                    </div>
                </div>
            </nav>

            {/* --- MOBILE MENU DROPDOWN --- */}
            <div className={`fixed inset-0 z-40 bg-white/95 backdrop-blur-xl transition-transform duration-300 md:hidden flex flex-col items-center justify-center space-y-8 ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
                
                {/* Standard Links */}
                {navLinks.map((link) => (
                    <Link
                        key={link.name}
                        to={link.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="text-2xl font-bold text-gray-800 hover:text-pink-500"
                    >
                        {link.name}
                    </Link>
                ))}

                {/* ✅ DASHBOARD LINK (Mobile) */}
                {isAuthenticated && (
                    <Link
                        to="/admin"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="text-2xl font-bold text-purple-600 hover:text-purple-800 flex items-center gap-2"
                    >
                        <LayoutDashboard /> Dashboard
                    </Link>
                )}

                <div className="flex flex-col items-center gap-6 mt-4">
                    {/* Cart */}
                    <button className="relative p-4 bg-pink-50 rounded-full">
                        <ShoppingCart className="w-8 h-8 text-pink-600" />
                        <span className="absolute top-0 right-0 w-6 h-6 bg-purple-500 text-white text-xs font-bold flex items-center justify-center rounded-full border-2 border-white">
                            3
                        </span>
                    </button>

                    {/* ✅ LOGIN / LOGOUT LOGIC (Mobile) */}
                    {isAuthenticated ? (
                         <button 
                            onClick={handleLogOut}
                            className="px-8 py-3 font-bold text-white rounded-full bg-gradient-to-r from-gray-700 to-gray-900 shadow-lg"
                        >
                            Logout
                        </button>
                    ) : (
                        // ✅ FIX: Wrapped in Link so it actually navigates!
                        <Link to="/login" onClick={() => setIsMobileMenuOpen(false)}>
                            <button className="px-8 py-3 font-bold text-white rounded-full bg-gradient-to-r from-pink-500 to-purple-500 shadow-lg">
                                Login / Sign Up
                            </button>
                        </Link>
                    )}
                </div>
            </div>
        </>
    );
};

export default MenuBar;